import React, { createContext, useContext, useEffect, useState } from 'react';
import { ThemeProvider as MuiThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import appTheme from '../lib/theme';

type ColorMode = 'dark' | 'light' | 'system';

type ThemeProviderProps = {
  children: React.ReactNode;
  defaultTheme?: ColorMode;
  storageKey?: string;
};

type ThemeProviderState = {
  colorMode: ColorMode;
  setColorMode: (colorMode: ColorMode) => void;
};

const initialState: ThemeProviderState = {
  colorMode: 'system',
  setColorMode: () => null,
};

const ThemeProviderContext = createContext<ThemeProviderState>(initialState);

export function ThemeProvider({
  children,
  defaultTheme = 'system',
  storageKey = 'vite-ui-theme',
  ...props
}: ThemeProviderProps) {
  const [colorMode, setColorMode] = useState<ColorMode>(
    () => (localStorage.getItem(storageKey) as ColorMode) || defaultTheme
  );

  // Determine if we should use dark mode
  const [isDarkMode, setIsDarkMode] = useState(false);

  useEffect(() => {
    const root = window.document.documentElement;
    root.classList.remove('light', 'dark');

    let effectiveTheme = colorMode;
    if (colorMode === 'system') {
      effectiveTheme = window.matchMedia('(prefers-color-scheme: dark)').matches
        ? 'dark'
        : 'light';
    }

    root.classList.add(effectiveTheme);
    setIsDarkMode(effectiveTheme === 'dark');
  }, [colorMode]);

  // Create a theme instance with the appropriate mode
  const theme = React.useMemo(() => {
    return createTheme({
      ...appTheme,
      palette: {
        ...appTheme.palette,
        mode: isDarkMode ? 'dark' : 'light',
        ...(isDarkMode && {
          background: {
            default: '#121212',
            paper: '#1e1e1e',
          },
          text: {
            primary: '#f3f4f6', // gray-100
            secondary: '#d1d5db', // gray-300
          },
        }),
      },
    });
  }, [isDarkMode]);

  const value = {
    colorMode,
    setColorMode: (colorMode: ColorMode) => {
      localStorage.setItem(storageKey, colorMode);
      setColorMode(colorMode);
    },
  };

  return (
    <ThemeProviderContext.Provider {...props} value={value}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeProviderContext.Provider>
  );
}

export const useTheme = () => {
  const context = useContext(ThemeProviderContext);

  if (context === undefined)
    throw new Error('useTheme must be used within a ThemeProvider');

  return context;
};
